import axios from "axios";
import { toast } from "react-toastify";

const API_URL = import.meta.env.VITE_API_URL;

// Create and configure axios instance
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // Important for cookies
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const message = error.response?.data?.message || "An error occurred";

    // Show error toast
    toast.error(message);

    return Promise.reject(error);
  },
);

// Auth API calls
export const login = async (credentials) => {
  try {
    const response = await api.post("/auth/login", credentials);
    toast.success(response.data.message || "Login successful");
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const logout = async () => {
  try {
    const response = await api.post("/auth/logout");
    toast.success(response.data.message || "Logged out successfully");
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getProfile = async () => {
  try {
    const response = await api.get("/auth/profile");
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const register = async (userData) => {
  try {
    const response = await api.post("/auth/register", userData);
    toast.success(response.data.message || "Registration successful");
    return response.data;
  } catch (error) {
    throw error;
  }
};

// User API calls
export const getUsers = () => api.get("/users");
export const getUserById = (id) => api.get(`/users/${id}`);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);

// Club API calls with improved error handling
export const getClubs = async () => {
  try {
    const response = await api.get("/clubs");
    if (!response?.data) {
      throw new Error("Invalid response format");
    }
    return response;
  } catch (error) {
    console.error("Error fetching clubs:", error);
    throw error;
  }
};

export const getClub = async (id) => {
  try {
    const response = await api.get(`/clubs/${id}`);
    if (!response?.data) {
      throw new Error("Invalid response format");
    }
    return response;
  } catch (error) {
    console.error("Error fetching club:", error);
    throw error;
  }
};

export const createClub = async (data) => {
  try {
    const response = await api.post("/clubs", data);
    if (!response?.data) {
      throw new Error("Invalid response format");
    }
    return response;
  } catch (error) {
    console.error("Error creating club:", error);
    throw error;
  }
};

export const updateClub = async (id, data) => {
  try {
    if (!id) {
      throw new Error("Club ID is required for update");
    }

    const response = await api.put(`/clubs/${id}`, {
      _id: id, // Include the ID in the request body
      ...data,
    });

    if (!response?.data) {
      throw new Error("Invalid response format");
    }

    return response;
  } catch (error) {
    console.error("Error updating club:", error);
    const errorMessage =
      error.response?.data?.message || error.message || "Failed to update club";
    throw new Error(errorMessage);
  }
};

export const deleteClub = async (id) => {
  try {
    if (!id) {
      throw new Error("Club ID is required");
    }

    const response = await api.delete(`/clubs/${id}`, {
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });

    if (!response?.data) {
      throw new Error("Invalid response format");
    }
    return response;
  } catch (error) {
    console.error("Error deleting club:", error);
    throw error;
  }
};

// Lead API calls
export const getLeads = async () => {
  try {
    const response = await api.get("/leads");
    return response.data;
  } catch (error) {
    console.error("Error fetching leads:", error);
    throw error;
  }
};

export const getLead = async (id) => {
  try {
    const response = await api.get(`/leads/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching lead:", error);
    throw error;
  }
};

export const submitLead = async (data) => {
  try {
    const response = await api.post("/leads", data);
    return response.data;
  } catch (error) {
    console.error("Error creating lead:", error);
    throw error;
  }
};

export const updateLead = async (id, data) => {
  try {
    const response = await api.put(`/leads/${id}`, data);
    return response.data;
  } catch (error) {
    console.error("Error updating lead:", error);
    throw error;
  }
};

export const deleteLead = async (id) => {
  try {
    const response = await api.delete(`/leads/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting lead:", error);
    throw error;
  }
};

export const approveLead = async (id, data) => {
  try {
    const response = await api.post(`/leads/${id}/approve`, data);
    return response.data;
  } catch (error) {
    console.error("Error approving lead:", error);
    throw error;
  }
};

export const rejectLead = async (id, data) => {
  try {
    const response = await api.post(`/leads/${id}/reject`, data);
    return response.data;
  } catch (error) {
    console.error("Error rejecting lead:", error);
    throw error;
  }
};

export const assignLead = async (id, data) => {
  try {
    const response = await api.put(`/leads/${id}/assign`, data);
    return response.data;
  } catch (error) {
    console.error("Error assigning lead:", error);
    throw error;
  }
};

// Notification API calls
export const getNotifications = async (userId) => {
  try {
    const url = userId ? `/notifications/${userId}` : "/notifications";
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error("Error fetching notifications:", error);
    throw error;
  }
};

export const markNotificationAsRead = async (id) => {
  try {
    const response = await api.put(`/notifications/${id}/read`);
    return response.data;
  } catch (error) {
    console.error("Error marking notification as read:", error);
    throw error;
  }
};

export const markAllNotificationsAsRead = async () => {
  try {
    const response = await api.put("/notifications/read-all");
    return response.data;
  } catch (error) {
    console.error("Error marking all notifications as read:", error);
    throw error;
  }
};

export const deleteNotification = async (id) => {
  try {
    const response = await api.delete(`/notifications/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting notification:", error);
    throw error;
  }
};

// Admin Dashboard API calls
export const getAdminStats = async () => {
  try {
    const response = await api.get("/admin/stats");
    return response.data;
  } catch (error) {
    console.error("Error fetching admin stats:", error);
    throw error;
  }
};

export const getClubAnalytics = async (clubId) => {
  try {
    const response = await api.get(`/admin/clubs/${clubId}/analytics`);
    return response.data;
  } catch (error) {
    console.error("Error fetching club analytics:", error);
    throw error;
  }
};

export const updateClubTokens = async (clubId, amount, isAdding) => {
  try {
    const response = await api.put(`/admin/clubs/${clubId}/tokens`, {
      amount,
      operation: isAdding ? "add" : "deduct",
    });
    return response.data;
  } catch (error) {
    console.error("Error updating club tokens:", error);
    throw error;
  }
};

export const sendClubNotification = async (data) => {
  try {
    const response = await api.post("/admin/notifications", data);
    return response.data;
  } catch (error) {
    console.error("Error sending notification:", error);
    throw error;
  }
};

// Single export statement with all functions and the api instance
export { api };
